import React, { useState, useEffect } from "react";
import {
    Modal,
    Stack,
    Tabs,
    TextInput,
    ColorInput,
    Button,
    Group,
    Paper,
    Text,
    ActionIcon,
    Select,
    Switch,
    Table,
    Badge
} from "@mantine/core";
import { modals } from "@mantine/modals";
import { notifications } from "@mantine/notifications";
import { IconTrash, IconGripVertical, IconPlus, IconShield, IconColumns } from "@tabler/icons-react";
import {
    createColumn,
    updateColumn,
    deleteColumn,
    getPermissions,
    createPermission,
    deletePermission
} from "../../services/workflowService";

const WorkflowSettings = ({ opened, onClose, columns, users, onUpdate }) => {
    const [localColumns, setLocalColumns] = useState([]);
    const [permissions, setPermissions] = useState([]);
    const [newColumnName, setNewColumnName] = useState("");
    const [newColumnColor, setNewColumnColor] = useState("#3b82f6");
    const [selectedUser, setSelectedUser] = useState(null);
    const [selectedViewUser, setSelectedViewUser] = useState(null);
    const [canEdit, setCanEdit] = useState(false);
    const [canDelete, setCanDelete] = useState(false);
    const [canAssign, setCanAssign] = useState(false);

    useEffect(() => {
        setLocalColumns([...columns]);
        loadPermissions();
    }, [columns]);

    const loadPermissions = async () => {
        try {
            const res = await getPermissions();
            if (res.success) {
                setPermissions(res.permissions);
            }
        } catch (error) {
            console.error("Error loading permissions:", error);
        }
    };

    const handleCreateColumn = async () => {
        if (!newColumnName.trim()) return;

        try {
            await createColumn({
                name: newColumnName,
                color: newColumnColor
            });
            setNewColumnName("");
            setNewColumnColor("#3b82f6");
            onUpdate();
        } catch (error) {
            console.error("Error creating column:", error);
        }
    };

    const handleUpdateColumn = async (columnId, data) => {
        try {
            await updateColumn(columnId, data);
            onUpdate();
        } catch (error) {
            console.error("Error updating column:", error);
        }
    };

    const handleDeleteColumn = async (columnId) => {
        modals.openConfirmModal({
            title: (
                <Group gap="xs">
                    <IconColumns size={18} color="red" />
                    <Text fw={700}>Видалити колонку</Text>
                </Group>
            ),
            centered: true,
            children: (
                <Text size="sm">
                    Ви впевнені, що хочете видалити цю колонку? Усі завдання в цій колонці також будуть видалені. Цю дію неможливо скасувати.
                </Text>
            ),
            labels: { confirm: 'Видалити', cancel: 'Скасувати' },
            confirmProps: { color: 'red', variant: 'filled' },
            onConfirm: async () => {
                try {
                    await deleteColumn(columnId);
                    onUpdate();
                } catch (error) {
                    console.error("Error deleting column:", error);
                    notifications.show({
                        title: 'Помилка видалення колонки',
                        message: error.response?.data?.message || "Щось пішло не так",
                        color: 'red'
                    });
                }
            },
        });
    };

    const handleCreatePermission = async () => {
        if (!selectedUser || !selectedViewUser) {
            alert("Оберіть обох користувачів");
            return;
        }

        try {
            await createPermission({
                userId: parseInt(selectedUser),
                canViewUserId: parseInt(selectedViewUser),
                canEdit,
                canDelete,
                canAssign
            });
            setSelectedUser(null);
            setSelectedViewUser(null);
            setCanEdit(false);
            setCanDelete(false);
            setCanAssign(false);
            loadPermissions();
        } catch (error) {
            console.error("Error creating permission:", error);
        }
    };

    const handleDeletePermission = async (permissionId) => {
        modals.openConfirmModal({
            title: (
                <Group gap="xs">
                    <IconShield size={18} color="red" />
                    <Text fw={700}>Відкликати права</Text>
                </Group>
            ),
            centered: true,
            children: (
                <Text size="sm">
                    Ви впевнені, що хочете відкликати ці права? Користувач більше не зможе керувати цими завданнями.
                </Text>
            ),
            labels: { confirm: 'Відкликати', cancel: 'Скасувати' },
            confirmProps: { color: 'red', variant: 'filled' },
            onConfirm: async () => {
                try {
                    await deletePermission(permissionId);
                    loadPermissions();
                } catch (error) {
                    console.error("Error deleting permission:", error);
                }
            },
        });
    };

    const userOptions = users.map((user) => ({
        value: user.id.toString(),
        label: user.email
    }));

    console.log('👥 WorkflowSettings users:', users.length, 'userOptions:', userOptions.length);

    return (
        <Modal
            opened={opened}
            onClose={onClose}
            title="Налаштування Воркфлоу"
            size="lg"
        >
            <Tabs defaultValue="columns">
                <Tabs.List>
                    <Tabs.Tab value="columns">Колонки</Tabs.Tab>
                    <Tabs.Tab value="permissions">Права доступу</Tabs.Tab>
                </Tabs.List>

                <Tabs.Panel value="columns" pt="md">
                    <Stack gap="md">
                        {/* Create Column */}
                        <Paper p="md" withBorder>
                            <Text fw={500} mb="sm">
                                Створити нову колонку
                            </Text>
                            <Group align="flex-end">
                                <TextInput
                                    label="Назва колонки"
                                    placeholder="Введіть назву..."
                                    value={newColumnName}
                                    onChange={(e) => setNewColumnName(e.target.value)}
                                    style={{ flex: 1 }}
                                />
                                <ColorInput
                                    label="Колір"
                                    value={newColumnColor}
                                    onChange={setNewColumnColor}
                                    style={{ width: 120 }}
                                />
                                <Button
                                    leftSection={<IconPlus size={14} />}
                                    onClick={handleCreateColumn}
                                >
                                    Додати
                                </Button>
                            </Group>
                        </Paper>

                        {/* Existing Columns */}
                        <Stack gap="xs">
                            {localColumns.map((column) => (
                                <Paper key={column.id} p="md" withBorder>
                                    <Group justify="space-between">
                                        <Group gap="xs">
                                            <ActionIcon variant="subtle" style={{ cursor: "grab" }}>
                                                <IconGripVertical size={16} />
                                            </ActionIcon>
                                            <div
                                                style={{
                                                    width: 16,
                                                    height: 16,
                                                    borderRadius: "50%",
                                                    backgroundColor: column.color
                                                }}
                                            />
                                            <Text fw={500}>{column.name}</Text>
                                        </Group>
                                        <Group gap="xs">
                                            <ColorInput
                                                value={column.color}
                                                onChange={(color) =>
                                                    handleUpdateColumn(column.id, { color })
                                                }
                                                style={{ width: 100 }}
                                            />
                                            <ActionIcon
                                                color="red"
                                                variant="light"
                                                onClick={() => handleDeleteColumn(column.id)}
                                            >
                                                <IconTrash size={16} />
                                            </ActionIcon>
                                        </Group>
                                    </Group>
                                </Paper>
                            ))}
                        </Stack>
                    </Stack>
                </Tabs.Panel>

                <Tabs.Panel value="permissions" pt="md">
                    <Stack gap="md">
                        {/* Create Permission Card */}
                        <Paper p="xl" withBorder radius="md" bg="var(--mantine-color-gray-0)">
                            <Stack gap="lg">
                                <Group justify="space-between">
                                    <Group gap="xs">
                                        <IconShield size={24} color="var(--mantine-color-blue-filled)" />
                                        <Text fw={700} size="lg">Надати права доступу</Text>
                                    </Group>
                                    <Badge variant="light" color="blue" size="lg" radius="sm">Адміністраторська дія</Badge>
                                </Group>

                                <Text size="sm" c="dimmed">
                                    Виберіть користувача, якому ви хочете надати доступ, та чиї завдання він зможе бачити та редагувати.
                                </Text>

                                <Group grow align="flex-start">
                                    <Select
                                        label="Хто отримує доступ"
                                        placeholder="Оберіть користувача..."
                                        data={userOptions}
                                        value={selectedUser}
                                        onChange={setSelectedUser}
                                        searchable
                                    />
                                    <Stack gap={0} justify="center" align="center" mt={30} style={{ maxWidth: 40, flex: 'none' }}>
                                        <div style={{ height: 2, width: 20, backgroundColor: 'var(--mantine-color-gray-3)' }}></div>
                                        <Text size="xs" fw={700} c="dimmed">──▶</Text>
                                    </Stack>
                                    <Select
                                        label="Чиї завдання бачить"
                                        placeholder="Оберіть користувача..."
                                        data={userOptions}
                                        value={selectedViewUser}
                                        onChange={setSelectedViewUser}
                                        searchable
                                    />
                                </Group>

                                {selectedUser && selectedViewUser && (
                                    <Paper p="xs" withBorder bg="blue.0" style={{ borderColor: 'var(--mantine-color-blue-2)' }}>
                                        <Text size="sm" fw={500} c="blue.8" ta="center">
                                            Користувач <b>{userOptions.find(u => u.value === selectedUser)?.label}</b> зможе керувати завданнями <b>{userOptions.find(u => u.value === selectedViewUser)?.label}</b>
                                        </Text>
                                    </Paper>
                                )}

                                <Stack gap="xs">
                                    <Text fw={600} size="sm">Додаткові дозволи:</Text>
                                    <Group gap="xl">
                                        <Stack gap={4}>
                                            <Switch
                                                label="Редагування"
                                                checked={canEdit}
                                                onChange={(e) => setCanEdit(e.currentTarget.checked)}
                                                styles={{ label: { fontWeight: 500 } }}
                                            />
                                            <Text size="xs" c="dimmed" ml={38}>Може змінювати деталі завдання</Text>
                                        </Stack>
                                        
                                        <Stack gap={4}>
                                            <Switch
                                                label="Видалення"
                                                checked={canDelete}
                                                onChange={(e) => setCanDelete(e.currentTarget.checked)}
                                                styles={{ label: { fontWeight: 500 } }}
                                            />
                                            <Text size="xs" c="dimmed" ml={38}>Може назавжди видаляти завдання</Text>
                                        </Stack>

                                        <Stack gap={4}>
                                            <Switch
                                                label="Призначення"
                                                checked={canAssign}
                                                onChange={(e) => setCanAssign(e.currentTarget.checked)}
                                                styles={{ label: { fontWeight: 500 } }}
                                            />
                                            <Text size="xs" c="dimmed" ml={38}>Може змінювати виконавця</Text>
                                        </Stack>
                                    </Group>
                                </Stack>

                                <Button 
                                    size="md" 
                                    onClick={handleCreatePermission}
                                    leftSection={<IconShield size={18} />}
                                    disabled={!selectedUser || !selectedViewUser}
                                >
                                    Підтвердити та надати доступ
                                </Button>
                            </Stack>
                        </Paper>

                        {/* Existing Permissions Table */}
                        <Paper withBorder radius="md">
                            <Table verticalSpacing="sm">
                                <Table.Thead bg="var(--mantine-color-gray-0)">
                                    <Table.Tr>
                                        <Table.Th>Користувач</Table.Th>
                                        <Table.Th>Бачить завдання</Table.Th>
                                        <Table.Th>Дозволи</Table.Th>
                                        <Table.Th style={{ width: 80 }}>Дії</Table.Th>
                                    </Table.Tr>
                                </Table.Thead>
                                <Table.Tbody>
                                    {permissions.length === 0 ? (
                                        <Table.Tr>
                                            <Table.Td colSpan={4} pt="xl">
                                                <Text ta="center" c="dimmed" italic>
                                                    Список прав доступу порожній
                                                </Text>
                                            </Table.Td>
                                        </Table.Tr>
                                    ) : (
                                        permissions.map((perm) => (
                                            <Table.Tr key={perm.id}>
                                                <Table.Td>
                                                    <Text fw={500}>{perm.user?.email}</Text>
                                                </Table.Td>
                                                <Table.Td>
                                                    <Text c="dimmed">{perm.viewableUser?.email}</Text>
                                                </Table.Td>
                                                <Table.Td>
                                                    <Group gap="xs">
                                                        {perm.canEdit && (
                                                            <Badge size="sm" color="blue" variant="outline">
                                                                Редагування
                                                            </Badge>
                                                        )}
                                                        {perm.canDelete && (
                                                            <Badge size="sm" color="red" variant="outline">
                                                                Видалення
                                                            </Badge>
                                                        )}
                                                        {perm.canAssign && (
                                                            <Badge size="sm" color="green" variant="outline">
                                                                Призначення
                                                            </Badge>
                                                        )}
                                                        {!perm.canEdit && !perm.canDelete && !perm.canAssign && (
                                                            <Badge size="sm" color="gray" variant="light">
                                                                Тільки перегляд
                                                            </Badge>
                                                        )}
                                                    </Group>
                                                </Table.Td>
                                                <Table.Td>
                                                    <ActionIcon
                                                        color="red"
                                                        variant="subtle"
                                                        onClick={() => handleDeletePermission(perm.id)}
                                                        title="Відкликати права"
                                                    >
                                                        <IconTrash size={18} />
                                                    </ActionIcon>
                                                </Table.Td>
                                            </Table.Tr>
                                        ))
                                    )}
                                </Table.Tbody>
                            </Table>
                        </Paper>
                    </Stack>
                </Tabs.Panel>
            </Tabs>
        </Modal>
    );
};

export default WorkflowSettings;
